var AnalyticalCompositionToPositionProvider =
/** @class */
function () {
  function AnalyticalCompositionToPositionProvider() {
    this.dimensions = 4;

    this.compositionToPosition = function (_a) {
      var a = _a[0],
          b = _a[1],
          c = _a[2],
          d = _a[3];
      var scale = 30;
      var x = (c + 1 - b) / 2;
      var y = Math.sqrt(3) / 2 * a + Math.sqrt(3) / 6 * d;
      var z = Math.sqrt(3) / 2 * d;
      return [scale * (x - 0.5), scale * (y - Math.sqrt(3) / 3), scale * (z - Math.sqrt(3) / 4)];
    };
  }

  AnalyticalCompositionToPositionProvider.prototype.setFunction = function (dimensions, fn) {
    this.dimensions = dimensions;
    this.compositionToPosition = fn;
  };

  AnalyticalCompositionToPositionProvider.prototype.getPosition = function (composition) {
    return this.compositionToPosition(composition);
  };

  AnalyticalCompositionToPositionProvider.prototype.getDimensions = function () {
    return this.dimensions;
  };

  return AnalyticalCompositionToPositionProvider;
}();

export { AnalyticalCompositionToPositionProvider };

var NearestCompositionToPositionProvider =
/** @class */
function () {
  function NearestCompositionToPositionProvider() {
    this.compositionMap = {};
  }

  NearestCompositionToPositionProvider.prototype.setData = function (dimensions, resolution, data, fractional) {
    if (fractional === void 0) {
      fractional = true;
    }

    if (!fractional) {
      resolution /= 100;
    }

    this.resolution = resolution;
    this.dimensions = dimensions;
    this.dimensions;
    this.decimalDigits = Math.ceil(-Math.log10(resolution));
    var n = data.length;
    var chunk = dimensions + 3;

    if (n % chunk !== 0) {
      throw new Error("The provided data does not match the expected size for a " + dimensions + "-dimensional set");
    }

    var nCompositions = n / chunk;

    for (var i = 0; i < nCompositions; ++i) {
      var composition = data.slice(i * chunk, i * chunk + dimensions);

      if (!fractional) {
        composition = composition.map(function (val) {
          return val / 100;
        });
      }

      var key = this.compositionToKey(composition);
      var position = data.slice(i * chunk + dimensions, (i + 1) * chunk);
      this.compositionMap[key] = position;
    }
  };

  NearestCompositionToPositionProvider.prototype.getPosition = function (composition) {
    var key = this.compositionToKey(composition);

    if (key in this.compositionMap) {
      return this.compositionMap[key];
    }

    return [0, 0, 0];
  };

  NearestCompositionToPositionProvider.prototype.getDimensions = function () {
    return this.dimensions;
  };

  NearestCompositionToPositionProvider.prototype.compositionToKey = function (composition) {
    var _this = this;

    return composition.map(function (val) {
      return _this.resolution * Math.round(val / _this.resolution);
    }).map(function (val) {
      return val.toFixed(_this.decimalDigits);
    }).join(',');
  };

  return NearestCompositionToPositionProvider;
}();

export { NearestCompositionToPositionProvider };

var DataProvider =
/** @class */
function () {
  function DataProvider(dimensions) {
    this.samples = [];
    this.scalars = new Set();
    this.elements = new Set();
    this.axisToIdx = {};
    this.idxToAxis = {};

    this.filter = function () {
      return true;
    };

    this.dimensions = dimensions;
  }

  DataProvider.prototype.setData = function (samples) {
    var _this = this;

    this.samples = [];
    this.scalars = new Set();

    if (samples.length === 0) {
      return;
    }

    for (var _i = 0, samples_1 = samples; _i < samples_1.length; _i++) {
      var sample = samples_1[_i];
      sample.fom.forEach(function (fom) {
        _this.scalars.add(fom.name);
      });
      Object.keys(sample.composition).forEach(function (element) {
        _this.elements.add(element);
      });
    }

    this.samples = samples;
    this.setActiveScalar(this.getDefaultScalar(this.getActiveScalar()));
  };

  DataProvider.prototype.getDefaultScalar = function (key) {
    if (key === void 0) {
      key = null;
    }

    if (this.scalars.has(key)) {
      return key;
    } else {
      try {
        return this.scalars.values().next().value;
      } catch (e) {
        return null;
      }
    }
  };

  DataProvider.prototype.getScalarRange = function (key) {
    var values = this.samples.map(function (sample) {
      return DataProvider.getSampleScalar(sample, key);
    });
    return [Math.min.apply(Math, values), Math.max.apply(Math, values)];
  };

  DataProvider.prototype.getScalars = function () {
    var scalars = [];
    this.scalars.forEach(function (val) {
      scalars.push(val);
    });
    return scalars;
  };

  DataProvider.prototype.getActiveScalar = function () {
    return this.activeScalar;
  };

  DataProvider.prototype.setActiveScalar = function (key) {
    if (this.scalars.has(key)) {
      this.activeScalar = key;
    } else {
      console.warn("Unable to set " + key + " as active scalar");
    }
  };

  DataProvider.prototype.getElements = function () {
    var elements = [];
    this.elements.forEach(function (val) {
      elements.push(val);
    });
    return elements;
  };

  DataProvider.prototype.setFilter = function (filter) {
    if (filter === void 0) {
      filter = null;
    }

    if (filter === undefined || filter === null) {
      filter = function filter() {
        return true;
      };
    }

    this.filter = filter;
  };

  DataProvider.prototype.getSamples = function (sampleFilter) {
    if (sampleFilter === void 0) {
      sampleFilter = undefined;
    }

    var filter = sampleFilter || this.filter;
    return this.samples.filter(function (sample) {
      return filter(sample);
    });
  };

  DataProvider.getSampleScalar = function (sample, scalar, runId, analysisId) {
    var matchRunId = !!runId;
    var matchAnalysisId = !!analysisId;

    for (var _i = 0, _a = sample.fom; _i < _a.length; _i++) {
      var fom = _a[_i];
      var isMatch = fom.name === scalar && (runId == fom.runId || !matchRunId) && (analysisId == fom.analysisId || !matchAnalysisId);

      if (isMatch) {
        return fom.value;
      }
    }

    return null;
  };

  DataProvider.isSelected = function (sample, selectedKeys) {
    return selectedKeys.has(sample._id);
  };

  return DataProvider;
}();

export { DataProvider };